<script lang="ts">
	import { ButtonV2, Icon, Text } from '@Shelly/shelly-design-system/index';
	import { createEventDispatcher } from 'svelte';
	import PromoModalBanner from './PromoModalBanner.svelte';
	import { format } from 'svelte-i18n';

	/** Banner image URL */
	export let image: string;
	/** Image alt text */
	export let alt: string = '';
	/** Card title */
	export let title: string;
	/** Card description text */
	export let description: string;
	/** FontAwesome icon class for the category tag */
	export let categoryIcon: string = 'fas fa-coins';
	/** FontAwesome icon class for the top-right decorative icon */
	export let cardIcon: string = 'fa-solid fa-percent';
	/** Expiry text shown on the banner (e.g. "Expires in 8 days"). Hidden when empty. */
	export let expiryText: string = '';
	/** Show "Active" badge */
	export let showActiveBadge: boolean = true;
	/** Active badge label on the banner */
	export let activeBadgeLabel: string = 'Active';
	/** "Learn more" button label */
	export let learnMoreLabel: string = 'Learn more';

	const dispatch = createEventDispatcher();

	$: categoryLabel = $format('label_promo', { default: 'PROMO' });
</script>

<div class={`promo-campaign-card ${showActiveBadge ? 'active' : 'expired'}`}>
	<PromoModalBanner
		{image}
		{alt}
		{activeBadgeLabel}
		{expiryText}
		{showActiveBadge}
		overlayPosition="top"
	/>
	<div class="promo-campaign-card__content">
		<div class="promo-campaign-card__header">
			<div class="promo-campaign-card__header-left">
				<div class="promo-campaign-card__category">
					<Icon icon={categoryIcon} size="xs" class="text-color-muted" />
					<span class="promo-campaign-card__category-label">{categoryLabel}</span>
				</div>
				<Text styleType="h2" class="promo-campaign-card__title">{title}</Text>
			</div>
			<Icon icon={cardIcon || 'fa-solid fa-percent'} class="promo-campaign-card__card-icon" />
		</div>
		<div class="promo-campaign-card__divider" role="separator" />
		<Text styleType="body-small" class="promo-campaign-card__description">{description}</Text>
		{#if showActiveBadge}
			<div class="promo-campaign-card__actions">
				<ButtonV2 size="m" variant="primary" on:click={() => dispatch('learnMore')}>
					{learnMoreLabel}
				</ButtonV2>
			</div>
		{/if}
	</div>
</div>

<style>
	.promo-campaign-card {
		background: var(--color-2, #303235);
		border-radius: var(--radius-small, 8px);
		overflow: hidden;
		display: flex;
		flex-direction: column;
	}

	.promo-campaign-card.expired {
		opacity: 0.6;
		user-select: none;
	}

	.promo-campaign-card__content {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-s, 8px);
		padding: var(--spacing-m, 16px);
	}

	.promo-campaign-card__header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		width: 100%;
		gap: var(--spacing-s, 8px);
	}

	.promo-campaign-card__header-left {
		display: flex;
		flex-direction: column;
		gap: 2px;
	}

	.promo-campaign-card__category {
		display: flex;
		align-items: center;
		gap: var(--spacing-xs, 4px);
	}

	.promo-campaign-card__category-label {
		font-family: 'Inter', sans-serif;
		font-size: 10px;
		font-weight: 500;
		line-height: 1;
		letter-spacing: 0.6px;
		text-transform: uppercase;
		color: var(--color-muted, #a1a2a2);
	}

	:global(.promo-campaign-card__title) {
		color: var(--color-white, #ffffff) !important;
		margin-top: 4px !important;
		margin-bottom: 0 !important;
	}

	:global(.promo-campaign-card__card-icon) {
		color: var(--color-white, #ffffff) !important;
		font-size: 28px !important;
		width: 44px;
		height: 44px;
		display: flex !important;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
	}

	.promo-campaign-card__divider {
		height: 1px;
		background: var(--color-3, #5b5f66);
		width: 100%;
		flex-shrink: 0;
	}

	:global(.promo-campaign-card__description) {
		color: var(--color-white, #ffffff) !important;
	}

	.promo-campaign-card__actions {
		display: flex;
		justify-content: flex-end;
		margin-top: var(--spacing-xs, 4px);
	}
</style>
